import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  ExternalLink,
  Inbox,
  RadioTower,
  RefreshCw,
} from "lucide-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState, type ElementType } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  recheckOutboxRecord,
  useOutboxRecords,
  usePublishTargets,
  type OutboxRecord,
  type PublishTargetStatus,
} from "@/hooks/usePublishVisibility";

const STATUS_OPTIONS = ["all", "pending", "ready", "failed", "sent"];

export default function OperatorPublishPage() {
  const [status, setStatus] = useState("all");
  const targetsQ = usePublishTargets();
  const queryClient = useQueryClient();
  const outboxQ = useOutboxRecords({
    status: status === "all" ? undefined : status,
    limit: 100,
  });
  const recheckQ = useMutation({
    mutationFn: recheckOutboxRecord,
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: ["outbox"] });
      void queryClient.invalidateQueries({ queryKey: ["publish-targets"] });
      void queryClient.invalidateQueries({ queryKey: ["operator-status"] });
      if (result.target_available) {
        toast.success("Outbox record is ready");
      } else {
        toast.warning(result.error_message ?? "Target is still unavailable");
      }
    },
    onError: (error) => {
      toast.error(`Outbox recheck failed: ${error.message}`);
    },
  });
  const targets = targetsQ.data?.items ?? [];
  const records = outboxQ.data?.items ?? [];
  const pendingCount = records.filter((record) => record.status === "pending").length;
  const failedCount = records.filter((record) => record.status === "failed").length;

  return (
    <div className="flex h-full flex-col">
      <header className="border-b border-border px-4 py-3 sm:px-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h1 className="text-xl font-semibold">Publish</h1>
            <p className="text-xs text-muted-fg">
              Target health and downstream publish attempts for reviewed candidates.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Link
              to="/candidates"
              className="inline-flex h-8 items-center gap-1.5 rounded-md border border-border px-2.5 text-xs font-medium hover:bg-muted"
            >
              <RadioTower className="size-3.5" />
              Candidates
            </Link>
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="h-8"
              onClick={() => {
                void targetsQ.refetch();
                void outboxQ.refetch();
              }}
            >
              <RefreshCw className="size-3.5" />
              Refresh
            </Button>
          </div>
        </div>
      </header>

      <div className="flex-1 overflow-auto p-4 sm:p-6">
        <div className="space-y-5">
          <div className="grid gap-3 sm:grid-cols-3">
            <PublishMetric
              icon={RadioTower}
              label="Targets"
              value={targets.length}
              tone={targets.some((target) => target.available) ? "ok" : "warn"}
            />
            <PublishMetric
              icon={Clock}
              label="Pending"
              value={pendingCount}
              tone={pendingCount > 0 ? "warn" : "ok"}
            />
            <PublishMetric
              icon={AlertTriangle}
              label="Failed"
              value={failedCount}
              tone={failedCount > 0 ? "error" : "ok"}
            />
          </div>

          <section className="overflow-hidden rounded-md border border-border bg-bg/40">
            <div className="border-b border-border/70 px-3 py-2.5">
              <h2 className="text-sm font-semibold">Targets</h2>
              <p className="text-xs text-muted-fg">
                Availability is read live from the configured downstream service.
              </p>
            </div>
            {targetsQ.isLoading ? (
              <EmptyPanel text="Loading targets..." />
            ) : targetsQ.isError ? (
              <EmptyPanel text={`Failed to load targets: ${(targetsQ.error as Error).message}`} danger />
            ) : targets.length === 0 ? (
              <EmptyPanel text="No publish targets configured." />
            ) : (
              <div className="divide-y divide-border/60">
                {targets.map((target) => (
                  <TargetRow key={target.id} target={target} />
                ))}
              </div>
            )}
          </section>

          <section className="overflow-hidden rounded-md border border-border bg-bg/40">
            <div className="flex flex-col gap-3 border-b border-border/70 px-3 py-2.5 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h2 className="text-sm font-semibold">Outbox</h2>
                <p className="text-xs text-muted-fg">
                  Recent publish attempts. Publish actions are created from candidates; outbox is diagnostics.
                </p>
              </div>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger className="h-8 w-full sm:w-36">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map((option) => (
                    <SelectItem key={option} value={option}>
                      {option === "all" ? "All statuses" : option}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {outboxQ.isLoading ? (
              <EmptyPanel text="Loading outbox..." />
            ) : outboxQ.isError ? (
              <EmptyPanel text={`Failed to load outbox: ${(outboxQ.error as Error).message}`} danger />
            ) : records.length === 0 ? (
              <EmptyPanel text="No publish attempts match this filter." />
            ) : (
              <div className="divide-y divide-border/60">
                {records.map((record) => (
                  <OutboxRow
                    key={record.id}
                    record={record}
                    recheckPending={recheckQ.isPending && recheckQ.variables === record.id}
                    onRecheck={() => recheckQ.mutate(record.id)}
                  />
                ))}
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}

function TargetRow({ target }: { target: PublishTargetStatus }) {
  return (
    <div className="flex flex-wrap items-center gap-3 px-3 py-2.5 text-sm">
      <div className="min-w-0 flex-1">
        <div className="font-medium">{target.display_name}</div>
        <div className="font-mono text-[11px] text-muted-fg">{target.id}</div>
      </div>
      <Badge variant={target.enabled ? "success" : "outline"}>
        {target.enabled ? "enabled" : "disabled"}
      </Badge>
      <Badge variant={target.available ? "success" : "danger"}>
        {target.available ? "available" : "down"}
      </Badge>
      <div className="min-w-0 basis-full text-xs text-muted-fg sm:basis-auto">
        {target.error_message ?? `modes: ${target.supported_modes.join(", ")}`}
      </div>
    </div>
  );
}

function OutboxRow({
  record,
  recheckPending,
  onRecheck,
}: {
  record: OutboxRecord;
  recheckPending: boolean;
  onRecheck: () => void;
}) {
  const candidate = candidatePayload(record);
  const symbol = typeof candidate?.symbol === "string" ? candidate.symbol : null;
  const source = typeof candidate?.source === "string" ? candidate.source : null;
  const errorMessage = record.last_error ?? stringValue(record.metadata.error_message);
  return (
    <div className="grid gap-2 px-3 py-3 text-sm lg:grid-cols-[180px_1fr_220px]">
      <div className="flex items-start gap-2">
        <StatusIcon status={record.status} />
        <div className="min-w-0">
          <Badge variant={statusBadge(record.status)}>{record.status}</Badge>
          <div className="mt-1 font-mono text-[11px] text-muted-fg">
            {record.destination_type}
          </div>
        </div>
      </div>
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          {symbol ? <span className="font-mono font-semibold">{symbol}</span> : null}
          {source ? <span className="text-xs text-muted-fg">{source}</span> : null}
          {record.destination_id ? (
            <span className="truncate font-mono text-[11px] text-muted-fg">
              {record.destination_id}
            </span>
          ) : null}
        </div>
        <div className="mt-1 truncate text-xs text-muted-fg">
          {errorMessage ?? notesFromCandidate(candidate) ?? "No validation errors recorded."}
        </div>
      </div>
      <div className="flex items-center justify-start gap-2 lg:justify-end">
        {record.status !== "sent" ? (
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-7 px-2 text-xs"
            disabled={recheckPending}
            onClick={onRecheck}
          >
            <RefreshCw className={recheckPending ? "size-3.5 animate-spin" : "size-3.5"} />
            Recheck
          </Button>
        ) : null}
        {record.inbox_id ? (
          <Link
            to={`/inbox?item=${encodeURIComponent(record.inbox_id)}`}
            className="inline-flex h-7 items-center gap-1 rounded-md border border-border px-2 text-xs hover:bg-muted"
          >
            <Inbox className="size-3.5" />
            Inbox
          </Link>
        ) : null}
        {symbol ? (
          <Link
            to={`/candidates?symbol=${encodeURIComponent(symbol)}`}
            className="inline-flex h-7 items-center gap-1 rounded-md border border-border px-2 text-xs hover:bg-muted"
          >
            <ExternalLink className="size-3.5" />
            Candidate
          </Link>
        ) : null}
        <span className="text-xs text-muted-fg">{formatDate(record.created_at)}</span>
      </div>
    </div>
  );
}

function PublishMetric({
  icon: Icon,
  label,
  value,
  tone,
}: {
  icon: ElementType;
  label: string;
  value: number;
  tone: "ok" | "warn" | "error";
}) {
  return (
    <div className="rounded-md border border-border bg-bg/50 p-3">
      <div className="flex items-center gap-2 text-xs text-muted-fg">
        <Icon className={metricIconClass(tone)} />
        {label}
      </div>
      <div className="mt-2 text-2xl font-semibold tabular-nums">{value}</div>
    </div>
  );
}

function EmptyPanel({ text, danger = false }: { text: string; danger?: boolean }) {
  const className = danger
    ? "px-3 py-8 text-center text-sm text-red-300"
    : "px-3 py-8 text-center text-sm text-muted-fg";
  return (
    <div className={className}>
      {text}
    </div>
  );
}

function StatusIcon({ status }: { status: string }) {
  if (status === "ready" || status === "sent") return <CheckCircle2 className="mt-0.5 size-4 text-emerald-300" />;
  if (status === "failed") return <AlertTriangle className="mt-0.5 size-4 text-red-300" />;
  return <Clock className="mt-0.5 size-4 text-amber-300" />;
}

function statusBadge(status: string): "success" | "danger" | "warn" | "outline" {
  if (status === "ready" || status === "sent") return "success";
  if (status === "failed") return "danger";
  if (status === "pending") return "warn";
  return "outline";
}

function candidatePayload(record: OutboxRecord): Record<string, unknown> | null {
  const value = record.payload.candidate;
  return isRecord(value) ? value : null;
}

function notesFromCandidate(candidate: Record<string, unknown> | null): string | null {
  const notes = candidate?.notes;
  return typeof notes === "string" && notes.trim() ? notes : null;
}

function stringValue(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value : null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function metricIconClass(tone: "ok" | "warn" | "error"): string {
  const base = "size-4";
  if (tone === "ok") return `${base} text-emerald-300`;
  if (tone === "warn") return `${base} text-amber-300`;
  return `${base} text-red-300`;
}

function formatDate(value: string | null): string {
  if (!value) return "never";
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "short",
    timeStyle: "short",
  }).format(new Date(value));
}
